import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Material UI
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

// Icons
import PaymentIcon from '@mui/icons-material/Payment';

// Components
import Modal from '../../../../../components/modal';
import ModalConfirm from '../../../../../components/modal/confirm';

// Hooks
import useStyles from './styles';
import useNotify from '../../../../../hooks/notify';
import useUser from '../../../../../hooks/user/useUser';
import useCart from '../../../../../hooks/orders/CartProvider';

// Models
import { PaymentStates } from '../../../../../models/PaymentStates';
import { PromotionState } from '../../../../../models/PromotionTypes';
import { OrderStates } from '../../../../../models/OrderEntity';
import cartService from '../../../../../services/cartService';

const InitiatePayment = () => {
    const { classes } = useStyles();
    const { push } = useNavigate();
    const { branch, cashRegister, pv } = useUser();
    const { Notify, variants } = useNotify();
    const { sale, setIgnoreDiscount } = useCart();

    const [loading, setLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState('');

    const initiatePayment = async (billingCode, cashRegisterId) => {
        setLoading(true);

        const req = {
            billingCode,
            orderId: sale.orderId,
            customer: sale.customer,
            cashRegisterId
        };

        return cartService
            .InitiatePayment(req)
            .catch(err => {
                setLoading(false);
                throw err;
            });
    }

    const validatePayment = () => {
        if (!sale) {
            return true;
        }
        if (!sale.customer) {
            return true;
        }
        if (sale.totalPayedAmount < sale.amount && !sale.payments?.some(p => p.state === PaymentStates.Initialized)) {
            return true;
        }
        if (sale.payments
            ?.filter(p => p.state === PaymentStates.Initialized)
            ?.reduce((acum, curr) => acum + curr.totalAmount, 0)
            .toFixed(2) !== (sale.amount - sale.totalPayedAmount).toFixed(2)) {
            return true
        }
        if (sale.amount <= 0) {
            return true;
        }
        if (!sale.items || sale.items.length === 0) {
            return true;
        }
 
        const hasExternalItems = sale.items.some(e => e.branchCode !== branch.code);
        if (hasExternalItems && !sale.shippingData) {
            return true;
        }

        if (sale.itemsToReturn?.length > 0 && !sale.observations && !sale.originalOrderId) {
            return true;
        }
        return false;
    }

    const doInitiatePayment = async () => {
        try {
            const paymentInitiated = await initiatePayment(pv.billingCode, cashRegister.id);
            const payment = paymentInitiated?.payments?.find(p => p.state === PaymentStates.Initialized)
            if (payment)
                window.location.href = payment.paymentUrl;
            else
                push(`/orders/detail/${sale.id}`);
        } catch (error) {
            Notify(`Ocurrió un error al iniciar el pago: ${error?.message}`, variants.ERROR);
        }

        setShowConfirm(false);
        setIgnoreDiscount([])
    }

    const handleInitiatePayment = async () => {
        const promotionsCouldBeApplied = sale.items.map(i =>
            i.promotions.unavaliables.filter(p => p.state === PromotionState.CouldBeApplied)).flat();

        if (promotionsCouldBeApplied.length === 0)
            await doInitiatePayment();
        else {
            const plural = promotionsCouldBeApplied.length > 1;
            setConfirmMessage(
                `Faltan productos para completar ${plural ? 'las' : 'la'} ${plural ? 'promociones' : 'promoción'} \n
                ${promotionsCouldBeApplied.map(p => `"${p.title.trim()}"`).join(", ")}
                ¿Desea continuar?`
            );
            setShowConfirm(true);
        }
    }

    return (
        <>
            {showConfirm &&
                <ModalConfirm
                    title="Atención"
                    open={showConfirm}
                    maxWidth="sm"
                    acceptText="Continuar con el pago"
                    cancelText="Cancelar"
                    message={confirmMessage}
                    onAccept={doInitiatePayment}
                    onCancel={() => setShowConfirm(false)}
                    onClose={() => setShowConfirm(false)}
                />}
            {(loading || sale.orderState === OrderStates.PaymentInitiated) &&
                <Modal open={loading || sale.orderState === OrderStates.PaymentInitiated}>
                    <Grid container direction='column' alignItems='center'>
                        <CircularProgress />
                        <Typography variant='h5'>
                            Inicializando pago...
                        </Typography>
                    </Grid>
                </Modal>}
            <Button
                className={classes.btnPayment}
                onClick={handleInitiatePayment}
                disabled={validatePayment()}
                variant='contained'
                color='primary'
                startIcon={<PaymentIcon />}
            >
                Iniciar pago
            </Button>
        </>
    )
}

export default InitiatePayment;